import { createRouter, createWebHistory } from 'vue-router';
import {
    ifNotAuthenticatedRedirect,
    ifAuthenticatedRedirect,
    hasQueryParamPasswordSelect,
    hasAccountId,
    hasPartnerAdminRights,
    hasAccountAdminRights,
    accountHasSubscription,
    accountHasNoSubscription,
    hasQueryParamsOrder,
    hasPaywall
} from '@/router/guards';

import AccountsView from '@/views/AccountsView';
import SettingsView from '@/views/account/SettingsView';
import SubscriptionsView from '@/views/account/SubscriptionsView';
import SubscriptionOrderView from '@/views/account/SubscriptionOrderView';
import SubscriptionChangeView from '@/views/account/SubscriptionChangeView';
import PaymentSuccessView from '@/views/account/PaymentSuccessView';
import PaymentFailedView from '@/views/account/PaymentFailedView';
import UsersView from '@/views/account/UsersView';
import PartnerUsersView from '@/views/admin/PartnerUsersView';
import ProfileView from '@/views/ProfileView';
import LoginView from '@/views/authentication/LoginView';
import RegisterView from '@/views/authentication/RegisterView';
import PasswordResetView from '@/views/authentication/PasswordResetView';
import PasswordSelectView from '@/views/authentication/PasswordSelectView';
import CompaniesView from '@/views/account/CompaniesView';
import CompanyView from '@/views/account/CompanyView';
import AudiencesView from '@/views/account/AudiencesView';
import AudienceView from '@/views/account/AudienceView';
import AudienceCompaniesView from '@/views/account/AudienceCompaniesView';
import AudienceCompanyView from '@/views/account/AudienceCompanyView';
import DashboardView from '@/views/account/DashboardView';
import OnboardingView from '@/views/account/OnboardingView.vue';
// import DocumentationView from '@/views/account/DocumentationView.vue';

const routes = [
    {
        path: '/',
        name: 'home',
        component: CompaniesView,
        beforeEnter: [ifNotAuthenticatedRedirect, ifAuthenticatedRedirect]
    },
    {
        path: '/onboarding',
        name: 'onboarding',
        component: OnboardingView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId],
    },
    // {
    //     path: '/documentation',
    //     name: 'documentation',
    //     component: DocumentationView,
    //     beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId],
    // },
    {
        path: '/settings',
        name: 'settings',
        component: SettingsView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId, hasPaywall],
    },
    {
        path: '/subscriptions',
        name: 'subscriptions',
        component: SubscriptionsView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId, hasAccountAdminRights],
    },
    {
        path: '/subscriptions/order',
        name: 'subscription-order',
        component: SubscriptionOrderView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId, hasAccountAdminRights, accountHasNoSubscription],
    },
    {
        path: '/subscriptions/change',
        name: 'subscription-change',
        component: SubscriptionChangeView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId, hasAccountAdminRights, accountHasSubscription],
    },
    {
        path: '/subscriptions/payment-success',
        name: 'payment-success',
        component: PaymentSuccessView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasQueryParamsOrder],
        props: (route) => ({ account_id: route.query.account_id, order_id: route.query.order_id })
    },
    {
        path: '/subscriptions/payment-failed',
        name: 'payment-failed',
        component: PaymentFailedView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasQueryParamsOrder],
        props: (route) => ({ account_id: route.query.account_id, order_id: route.query.order_id })
    },
    {
        path: '/users',
        name: 'users',
        component: UsersView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId, hasAccountAdminRights, hasPaywall],
    },
    {
        path: '/admins',
        name: 'admins',
        component: PartnerUsersView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasPartnerAdminRights],
    },
    {
        path: '/accounts',
        name: 'accounts',
        component: AccountsView,
        beforeEnter: ifNotAuthenticatedRedirect
    },
    {
        path: '/profile',
        name: 'profile',
        component: ProfileView,
        beforeEnter: ifNotAuthenticatedRedirect
    },
    {
        path: '/login',
        name: 'login',
        component: LoginView,
        beforeEnter: ifAuthenticatedRedirect
    },
    {
        path: '/register',
        name: 'register',
        component: RegisterView,
        beforeEnter: ifAuthenticatedRedirect
    },
    {
        path: '/reset-password',
        name: 'passwordReset',
        component: PasswordResetView,
        beforeEnter: ifAuthenticatedRedirect
    },
    {
        path: '/select-password',
        name: 'passwordSelect',
        component: PasswordSelectView,
        beforeEnter: [ifAuthenticatedRedirect, hasQueryParamPasswordSelect],
        props: (route) => ({ r: route.query.r })
    },
    {
        path: '/dashboard',
        name: 'dashboard',
        component: DashboardView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId]
    },
    {
        path: '/companies',
        name: 'companies',
        component: CompaniesView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId],
        props: { mode: 'all' }
    },
    {
        path: '/suspects',
        name: 'suspects',
        component: CompanyView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId, hasPaywall],
        props: { mode: 'new' }
    },
    {
        path: '/prospects',
        name: 'prospects',
        component: CompaniesView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId],
        props: (route) => ({ 
            mode: 'prospects',
            prospectOptions: route.query.prospectOptions
        })
    },
    {
        path: '/companies/:id',
        name: 'company',
        component: CompanyView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId],
        props: true
    },
    {
        path: '/audiences',
        name: 'audiences',
        component: AudiencesView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId]
    },
    {
        path: '/audiences/:id',
        name: 'audience',
        component: AudienceView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId],
        props: true
    },
    {
        path: '/audiences/:id/companies',
        name: 'audienceCompanies',
        component: AudienceCompaniesView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId],
        props: (route) => ({ audienceId: route.params.id, mode: 'all' })
    },
    {
        path: '/audiences/:id/suspects',
        name: 'audienceSuspects',
        component: AudienceCompanyView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId, hasPaywall],
        props: (route) => ({ audienceId: route.params.id, mode: 'new' })
    },
    {
        path: '/audiences/:id/prospects',
        name: 'audienceProspects',
        component: AudienceCompaniesView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId],
        props: (route) => ({
            audienceId: route.params.id,
            mode: 'prospects',
            prospectOptions: route.query.prospectOptions
        })
    },
    {
        path: '/audiences/:id/companies/:companyId',
        name: 'audienceCompany',
        component: AudienceCompanyView,
        beforeEnter: [ifNotAuthenticatedRedirect, hasAccountId],
        props: (route) => ({ id: route.params.companyId, audienceId: route.params.id })
    },
    {
        path: '/:catchAll(.*)',
        redirect: '/'
    }
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
});

export default router;
